// @ts-strict-ignore
import {AfterViewInit, Component, effect, inject, input, output} from "@angular/core";
import {AssessableUiService} from "../services/assessable-ui.service";
import {Translations} from "@translations";
import {AssessablePanelMode, AssessablePanelType} from "@common/components/assessment/utils";
import {AssessmentItem, ChecklistItemType, UserRecordingAssessment} from "@models";
import {SimAssessmentScoreService, SimConfigurationService, TrackByMethods} from "@services";
import {AssessmentContextService} from "../services/assessment-context.service";
import {AssessmentAnswerService} from "../services/assessment-answer.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {configureAssessmentProviders} from "../services/assessable.configuration";

export enum AssessmentAnswerPanelVariant {
	RecordingView = "recording-view",
	ExaminerView = "examiner-view",
	PeerPracticeView = "peer-practice-view",
	ConductedView = "conducted-view",
}

@UntilDestroy()
@Component({
	selector: "sim-assessment-answer-panel",
	templateUrl: "./assessment-answer-panel.component.html",
	styleUrl: "./assessment-answer-panel.component.scss",
	providers: [...configureAssessmentProviders()],
	standalone: false,
})
export class AssessmentAnswerPanelComponent implements AfterViewInit {
	// Enums
	protected readonly T = Translations;
	protected readonly AssessablePanelType = AssessablePanelType;
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly ChecklistItemType = ChecklistItemType;
	protected readonly TrackByMethods = TrackByMethods;
	protected readonly AssessmentAnswerPanelVariant = AssessmentAnswerPanelVariant;
	protected readonly SimConfigurationService = SimConfigurationService;

	// Injections
	protected contextService = inject(AssessmentContextService);
	protected store: AssessableUiService<AssessmentItem> = inject(AssessableUiService);
	protected answerService = inject(AssessmentAnswerService);
	private _simAssessmentScoreService = inject(SimAssessmentScoreService);

	// I/O
	public readonly assessable = input.required<UserRecordingAssessment>();
	public readonly mode = input.required<AssessablePanelMode>();
	/**
	 * The variant of the panel to display.
	 * - RecordingView: The view for the recording page.
	 * - ExaminerView: The view for the examiner device: reduced information, compact layout.
	 * - ConductedView: The view for the conducted assessments page: compact layout.
	 */
	public readonly variant = input.required<AssessmentAnswerPanelVariant>();
	public readonly isInArchive = input<boolean>(false);
	public readonly onJumpToItem = output<number>();

	readonly #changeModeEffect = effect(() => this.store.setMode(this.mode()));
	readonly #initContextEffect = effect(() => {
		this.contextService.initialize(this.assessable(), this.assessable().competenceCategoryIds);
		this.answerService.initialize(this.assessable);
	});

	public $changedAssessment = this.answerService.$assessable;
	public groupExpanded = this.store.groupExpanded;
	public itemExpanded = this.store.itemExpanded;
	public isFormInitialized = this.answerService.isInitialized;
	public isFormValid = this.contextService.isFormValid;

	public ngAfterViewInit() {
		this.answerService.$savingPipeline.pipe(untilDestroyed(this)).subscribe();
		this.store.expandFirstGroupAndItem();
	}

	public get note(): string {
		return this.answerService.form.controls.note.value;
	}

	public handleNoteChange(note: string) {
		this.answerService.form.controls.note.setValue(note);
	}

	public expandGroup(groupId: string) {
		const itemId = this.contextService.getAssessable().assessableGroups.find((group) => group.groupId === groupId)
			?.items[0]?.assessableItemId;
		if (!itemId) {
			return;
		}
		this.expandGroupAndItem(groupId, itemId);
	}

	public expandGroupAndItem(groupId: string, itemId: string) {
		this.store.handleGroupExpand(groupId);
		this.store.handleItemExpand(itemId);
		this.store.scrollToElement(itemId);
	}

	public getGroupScore(groupId: string): string {
		if (this.store.mode() === AssessablePanelMode.Edit) {
			return "-";
		}
		const assessment = this.contextService.getAssessable();
		const group = assessment.assessableGroups.find((group) => group.groupId === groupId);
		const groupScore = this._simAssessmentScoreService.getGroupScore(group, assessment);

		if (isNaN(groupScore)) {
			return "-";
		}
		return `${groupScore}`;
	}

	public getMaxGroupScore(groupId: string): string {
		const assessment = this.contextService.getAssessable();
		const group = assessment.assessableGroups.find((group) => group.groupId === groupId);
		if (!group) {
			return "-";
		}

		const groupMaxScore = this._simAssessmentScoreService.getGroupMaxScore(group);

		if (isNaN(groupMaxScore)) {
			return "-";
		}
		return `${groupMaxScore}`;
	}
}
